import { useCallback, useEffect, useRef, useState } from 'react';
import { kanal } from '@beholder/shared';
import type { Idee, IdeenDenkt, IdeenNachricht, IdeenSitzung, IdeenSitzungLive, IdeenZiele, PersonKurz, SitzungStand } from '@beholder/shared/module/ideen';
import { api } from '../../core/api';
import { live, useFluechtig, useLive, useLiveVerbunden } from '../../core/live';

/** API der Seite „Ideen“ (projektübergreifend). */
export const pfad = (rest = '') => `/api/modul/ideen${rest}`;
/** Adresse der Seite „Ideen“ in der Oberfläche. */
export const SEITE = '/modul/ideen';

/** Sitzungen ohne Projekt haben keinen Live-Kanal (Kern-Kanäle sind projektbezogen) – so oft wird nachgefragt. */
const ABFRAGE_SITZUNG_MS = 3000;
const ABFRAGE_LISTE_MS = 10000;

/** Alle 'ms' Millisekunden ausführen, solange die Seite sichtbar ist. */
function useAbfrage(fn: () => void, ms: number | null) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!ms) return;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') ref.current();
    }, ms);
    return () => clearInterval(t);
  }, [ms]);
}

/** Liste der eigenen Unterreiter (regelmäßig abgefragt). */
export function useSitzungen() {
  const [liste, setListe] = useState<IdeenSitzung[] | null>(null);
  const [fehler, setFehler] = useState('');
  const laden = useCallback(async () => {
    try {
      setListe(await api.get<IdeenSitzung[]>(pfad('/sitzungen')));
      setFehler('');
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Fehler');
    }
  }, []);
  useEffect(() => {
    void laden();
  }, [laden]);
  useAbfrage(() => void laden(), ABFRAGE_LISTE_MS);
  return { liste, setListe, fehler, laden };
}

/** Alle sichtbaren Ideen (optional nur eines Projekts). Mit Projekt live, sonst regelmäßig abgefragt. */
export function useAlleIdeen(projektId?: string) {
  const [ideen, setIdeen] = useState<Idee[] | null>(null);
  const [fehler, setFehler] = useState('');
  const laden = useCallback(async () => {
    try {
      setIdeen(await api.get<Idee[]>(pfad(projektId ? `/ideen?projekt=${projektId}` : '/ideen')));
      setFehler('');
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Fehler');
    }
  }, [projektId]);
  useEffect(() => {
    void laden();
  }, [laden]);
  useLive(projektId ? kanal(projektId, 'ideen') : null, () => void laden());
  useAbfrage(() => void laden(), projektId ? null : ABFRAGE_LISTE_MS);
  return { ideen, fehler, laden, setIdeen };
}

export function ersetzen(liste: Idee[], i: Idee): Idee[] {
  const da = liste.some((x) => x.id === i.id);
  return da ? liste.map((x) => (x.id === i.id ? i : x)) : [...liste, i];
}

export interface Tippend {
  id: string;
  name: string;
  bis: number;
}

/**
 * Eine Sitzung: Gespräch, Ideen, „denkt nach“. Einem Projekt zugeordnet: live (mit Anwesenden und „tippt“),
 * sonst alle paar Sekunden abgefragt.
 */
export function useSitzung(sitzungId: string, ichId: string) {
  const [sitzung, setSitzung] = useState<IdeenSitzung | null>(null);
  const [nachrichten, setNachrichten] = useState<IdeenNachricht[]>([]);
  const [ideen, setIdeen] = useState<Idee[]>([]);
  const [denkt, setDenkt] = useState<IdeenDenkt | null>(null);
  const [anwesend, setAnwesend] = useState<PersonKurz[]>([]);
  const [tippend, setTippend] = useState<Tippend[]>([]);
  const [fehler, setFehler] = useState('');
  const [weg, setWeg] = useState(false);
  const verbunden = useLiveVerbunden();

  const uebernehmen = (s: SitzungStand) => {
    setSitzung(s.sitzung);
    setNachrichten(s.nachrichten);
    setIdeen(s.ideen);
    setDenkt(s.denkt);
  };

  const laden = useCallback(async () => {
    try {
      const s = await api.get<SitzungStand>(pfad(`/sitzungen/${sitzungId}`));
      uebernehmen(s);
      setFehler('');
    } catch (e) {
      if ((e as { status?: number })?.status === 404) setWeg(true);
      else setFehler(e instanceof Error ? e.message : 'Fehler');
    }
  }, [sitzungId]);

  useEffect(() => {
    setSitzung(null);
    setNachrichten([]);
    setIdeen([]);
    setDenkt(null);
    setWeg(false);
    setTippend([]);
    setAnwesend([]);
    void laden();
  }, [laden]);

  // Live nur mit eigenem Leserecht im Projekt (der Kern prüft das beim Abo); sonst – auch bei Freigaben – abfragen
  const projektId = sitzung?.projektLesen ? sitzung.projektId : null;
  const k = projektId ? kanal(projektId, 'ideen', sitzungId) : null;
  // Ohne Projekt (oder ohne Live-Verbindung): regelmäßig nachfragen
  useAbfrage(() => void laden(), !k || !verbunden ? ABFRAGE_SITZUNG_MS : null);

  useLive(k, (d) => {
    const n = d as IdeenSitzungLive;
    switch (n.typ) {
      case 'stand':
        uebernehmen(n.stand);
        break;
      case 'sitzung':
        void laden();
        break;
      case 'nachricht':
        setNachrichten((l) => (l.some((x) => x.id === n.nachricht.id) ? l : [...l, n.nachricht]));
        if (n.nachricht.von) setTippend((t) => t.filter((x) => x.id !== n.nachricht.von!.id));
        break;
      case 'idee':
        setIdeen((l) => ersetzen(l, n.idee));
        break;
      case 'idee-weg':
        setIdeen((l) => l.filter((i) => i.id !== n.id));
        break;
      case 'denkt':
        setDenkt(n.denkt);
        break;
      case 'anwesend':
        setAnwesend(n.personen);
        break;
      case 'weg':
        // gelöscht oder aus dem Projekt genommen: neu laden entscheidet (404 = weg)
        void laden();
        break;
    }
  });

  useFluechtig(k, (d, von) => {
    if ((d as { typ?: string })?.typ !== 'tippt' || von.benutzerId === ichId) return;
    setTippend((t) => [...t.filter((x) => x.id !== von.benutzerId), { id: von.benutzerId, name: von.anzeigename, bis: Date.now() + 4000 }]);
  });

  useEffect(() => {
    if (!tippend.length) return;
    const t = setInterval(() => setTippend((l) => l.filter((x) => x.bis > Date.now())), 1000);
    return () => clearInterval(t);
  }, [tippend.length]);

  const letztesTippen = useRef(0);
  const tippe = useCallback(() => {
    if (!k) return;
    const jetzt = Date.now();
    if (jetzt - letztesTippen.current < 2000) return;
    letztesTippen.current = jetzt;
    live.fluechtig(k, { typ: 'tippt' });
  }, [k]);

  return { sitzung, setSitzung, nachrichten, ideen, setIdeen, denkt, anwesend, tippend, fehler, weg, tippe, laden, live: !!k };
}

export function useZiele(sitzungId: string | null, abh: unknown = null) {
  const [ziele, setZiele] = useState<IdeenZiele | null>(null);
  useEffect(() => {
    if (!sitzungId) return setZiele(null);
    api
      .get<IdeenZiele>(pfad(`/sitzungen/${sitzungId}/ziele`))
      .then(setZiele)
      .catch(() => setZiele(null));
  }, [sitzungId, abh]);
  return ziele;
}

export type Sortierung = 'stimmen' | 'neu' | 'alt';

export function sortieren(liste: Idee[], s: Sortierung): Idee[] {
  const l = [...liste];
  if (s === 'stimmen') l.sort((a, b) => b.punkte - a.punkte || b.stimmen.length - a.stimmen.length || a.erstelltAm.localeCompare(b.erstelltAm));
  if (s === 'neu') l.sort((a, b) => b.erstelltAm.localeCompare(a.erstelltAm));
  if (s === 'alt') l.sort((a, b) => a.erstelltAm.localeCompare(b.erstelltAm));
  return l;
}

/** „Nur ich“, „Geteilt mit Lea und Tom“, „Von Marc geteilt“, „Projekt „Sommerfest““ */
export function sichtbarkeitText(s: IdeenSitzung, ichId?: string): string {
  if (s.sichtbarkeit === 'projekt') {
    const zusatz = s.freigaben.length ? ` · geteilt mit ${s.freigaben.length === 1 ? s.freigaben[0]!.name : `${s.freigaben.length} Personen`}` : '';
    return `Projekt „${s.projektName ?? ''}“${zusatz}`;
  }
  if (s.sichtbarkeit === 'geteilt') {
    if (ichId && s.erstelltVon && s.erstelltVon.id !== ichId) return `Von ${s.erstelltVon.name} geteilt`;
    const namen = s.freigaben.map((f) => f.name);
    return `Geteilt mit ${namen.length <= 1 ? namen[0] ?? '' : `${namen.slice(0, -1).join(', ')} und ${namen.at(-1)}`}`;
  }
  return 'Nur ich';
}
